// app/components/InvestorOwnedInfoCard.tsx
"use client";

import * as React from "react";
import {
    Box,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography,
} from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";

const BORDER = "#e8ecf2";
const TEAL = "#16a596";

export default function InvestorOwnedInfoCard({
    data,
    surfaceColor = "#f7f9fc",
}) {
    const { title, items } = data;
    return (
        <Card
            elevation={0}
            sx={{
                position: "relative",
                borderRadius: 3,
                border: `1px solid ${BORDER}`,
                // IMPORTANT: allow notches to sit outside the border
                overflow: "visible",
                bgcolor: "#fff",
            }}
        >
            {/* Title bar with perfectly centered “ticket” notches */}
            <Box
                sx={{
                    position: "relative",
                    px: 2,
                    py: 1.25,
                    textAlign: "center",
                    borderBottom: `1px solid ${BORDER}`,
                    borderRadius: 3,
                    bgcolor: "#fff",
                    // notches
                    "&:before, &:after": {
                        content: '""',
                        position: "absolute",
                        top: "100%",
                        transform: "translateY(-50%)",
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        // use the PAGE surface color so it looks like a cut-out
                        backgroundColor: surfaceColor,
                        // fine outline to match card border
                        boxShadow: `inset 0 0 0 1px ${surfaceColor}`,
                        pointerEvents: "none",
                    },
                    // half outside the card edges; 12px = radius
                    "&:before": { left: -12 },
                    "&:after": { right: -12 },
                }}
            >
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#0b3c78" }}>
                    {title}
                </Typography>
            </Box>

            <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
                <List sx={{ m: 0, p: 0 }}>
                    {items.map((it, idx) => {
                        const hasNested = !!it.nested?.length;
                        return (
                            <ListItem key={idx} sx={{ alignItems: "flex-start", py: 0.75 }}>
                                <ListItemIcon sx={{ minWidth: 26, pt: "6px" }}>
                                    <CircleIcon sx={{ fontSize: 8, color: TEAL }} />
                                </ListItemIcon>

                                {hasNested ? (
                                    <ListItemText
                                        primary={
                                            <Box>
                                                <Typography
                                                    component="span"
                                                    sx={{ fontWeight: 700, fontSize: 15.5 }}
                                                >
                                                    {it.title}
                                                </Typography>
                                                <List disablePadding sx={{ mt: 0.75, ml: 2.25 }}>
                                                    {it.nested?.map((n, j) => (
                                                        <ListItem key={j} disableGutters sx={{ py: 0.25 }}>
                                                            <ListItemIcon sx={{ minWidth: 18, pt: "4px" }}>
                                                                <CircleIcon
                                                                    sx={{ fontSize: 6, color: "text.primary" }}
                                                                />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primaryTypographyProps={{
                                                                    fontSize: 14.5,
                                                                    lineHeight: 1.65,
                                                                }}
                                                                primary={n.text}
                                                            />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </Box>
                                        }
                                    />
                                ) : (
                                    <ListItemText
                                        primaryTypographyProps={{
                                            fontSize: 15.5,
                                            lineHeight: 1.75,
                                        }}
                                        primary={it.text}
                                    />
                                )}
                            </ListItem>
                        );
                    })}
                </List>
            </CardContent>
        </Card>
    );
}
